import { discountHelpers } from "@dotpe/rista-pos-common";
import type { DiscountApprovalType } from "../redux-store/slices/DiscountSlice/shared";
import type { Sale } from "./types";

/**
 * Manager approval, and the customer-verification gate that sits beside it.
 *
 * Both are CASHIER-side concerns: a customer-facing surface has no manager to ask and no cashier
 * to verify anyone, so `useKioskDiscounts` never reaches this file. It is kept separate from the
 * apply/remove rules for exactly that reason — the kiosk pays nothing for a POS-only stage.
 *
 * The arithmetic itself belongs to `@dotpe/rista-pos-common`, which both apps already share.
 * What lives here is the DECISION: given its answer, what should the surface do next. The old
 * component asked the library twice — once in `checkoutCart`, once in `saveToCart` — and drew
 * subtly different conclusions from the same numbers.
 */

/** What a surface must do about approval before it may proceed. */
export type ApprovalDecision =
  /** Nothing to ask. Carry on. */
  | { kind: "clear"; approvalData: DiscountApprovalType; totalDiscount: number }
  /**
   * Approval is needed. `autoRequest` is true where the surface should send the request itself
   * rather than showing a prompt — the behaviour web attached to `source !== "new-order"`.
   */
  | {
      kind: "approval-required";
      approvalData: DiscountApprovalType;
      totalDiscount: number;
      autoRequest: boolean;
      message?: string;
    }
  /**
   * The sale is closed and its discount exceeds the permitted limit, so there is nothing to
   * approve — the override is simply refused.
   */
  | { kind: "refused"; approvalData: DiscountApprovalType; message?: string };

export interface ApprovalContext {
  sale: Sale;
  /** `businessStore.userData.accessPreferences`. */
  accessPreferences: Record<string, any>;
  /** The approval rows already recorded for this sale. */
  currentApprovalData: DiscountApprovalType[];
  /** `saleutil.validateManualDiscountLimit`, from `validateManualDiscountLimitFn()`. */
  validateManualDiscountLimit: (...args: any[]) => Record<string, any>;
  /**
   * Whether this surface sends the approval request itself.
   *
   * ⚠️ Web derived this from `source !== "new-order"`: the table-view and my-order screens fire
   * the request on the cashier's behalf, while the new-order cart shows a prompt. Passed as a
   * fact rather than re-derived from a `source` string, so a new surface states its own
   * behaviour instead of matching a magic value.
   */
  autoRequest: boolean;
}

/**
 * What to do about approval before saving or checking out.
 *
 * ⚠️ **`closeModal` is not represented here.** The library returns it, and the old component
 * closed the discount modal on it — but it is true on BOTH the refused and the
 * approval-required paths, and on the latter web closed the modal and then immediately sent a
 * request whose callback closed it again. A surface that wants that behaviour can close on any
 * non-`clear` decision; the decision itself stays about approval.
 */
export const decideApproval = ({
  sale,
  accessPreferences,
  currentApprovalData,
  validateManualDiscountLimit,
  autoRequest,
}: ApprovalContext): ApprovalDecision => {
  const { validation, toastMessage, totalDiscount, shouldRender } =
    discountHelpers.discountLimitCheck(
      sale as any,
      accessPreferences,
      currentApprovalData[0]?.totalDiscount || 0,
      validateManualDiscountLimit as any,
    );

  const approvalData = discountHelpers.getUpdatedDiscountApprovalData({
    shouldRender,
    mid: (sale as any)?.mid,
    currentDiscountApprovalData: currentApprovalData as any,
    totalDiscount,
  }) as DiscountApprovalType;

  if (!validation) {
    return { kind: "refused", approvalData, message: toastMessage };
  }

  if (shouldRender && approvalData.isApprovalRequired) {
    return {
      kind: "approval-required",
      approvalData,
      totalDiscount,
      autoRequest,
      message: toastMessage,
    };
  }

  return { kind: "clear", approvalData, totalDiscount };
};

/**
 * Must the customer be verified before this bill's discounts may be saved?
 *
 * `discountValidation` answers true when verification is required and the sale carries no
 * customer at all — which cannot be resolved by sending an OTP, so it is reported rather than
 * prompted.
 *
 * @param authenticationRequired - `isDiscCustAuthReq()`, the merchant's setting for this bill.
 */
export const needsCustomerBeforeSave = (
  authenticationRequired: boolean,
  sale: Sale,
): boolean => !!discountHelpers.discountValidation(authenticationRequired, sale as any);

/** The approval rows recorded against THIS sale. */
export const getApprovalDataForSale = (
  approvalData: DiscountApprovalType[] = [],
  saleMid: string,
): DiscountApprovalType[] =>
  discountHelpers.getCurrentDiscountApprovalData(approvalData as any, saleMid) as DiscountApprovalType[];

/**
 * After a repricing lowered the bill's discount: the approval state to RESET to, or null when
 * there is nothing to reset. v1's `checkAndUpdateApprovalState` (both platforms' bodies are the
 * same, `helpers/updateDiscountApprovalState`, which keeps its own copy until its cutover) as a
 * pure rule, so the shared write path (`ordering/writePath`) asks it without a v1 helper (rule 14).
 *
 * ⚠️ It only ever RESETS: when the limit check says approval would still be asked (`shouldRender`),
 * nothing is written here, as v1's comment says ("only update here, if we are trying to reset").
 */
export const approvalResetAfterRepricing = ({
  sale,
  approvalData = [],
  accessPreferences = {},
  validateManualDiscountLimit,
}: {
  sale: Sale;
  approvalData?: DiscountApprovalType[];
  accessPreferences?: Record<string, any>;
  /** `saleutil.validateManualDiscountLimit` */
  validateManualDiscountLimit: (...args: any[]) => Record<string, any>;
}): DiscountApprovalType | null => {
  const mid = (sale as any)?.mid;
  const currentDiscountApprovalData = discountHelpers.getCurrentDiscountApprovalData(approvalData as any, mid);
  const { shouldRender, totalDiscount } = discountHelpers.discountLimitCheck(
    sale as any,
    accessPreferences,
    (sale as any)?.discountTotal,
    validateManualDiscountLimit as any,
  );
  if (shouldRender) return null;
  return discountHelpers.getUpdatedDiscountApprovalData({ shouldRender, mid, currentDiscountApprovalData, totalDiscount } as any) as DiscountApprovalType;
};
